import { Head } from '@inertiajs/react';
import type { ReactNode } from 'react';

export const SITE_URL = 'https://www.emovia.com';
const DEFAULT_IMAGE_URL = 'https://cdn.emovia.com/blog.png';

const baseJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Emovia',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: 'https://cdn.emovia.com/logo/emovia-logo-h48-primary-white.png',
      },
      email: 'info@emovia.com',
      description:
        'Emovia è un diario guidato con IA Interrogativa, progettato con psicologi per psicologi: raccoglie ciò che la persona vive tra le sedute e lo restituisce al professionista come materiale clinico ordinato.',
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'Emovia',
      publisher: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'it-IT',
    },
  ],
};

// Inertia <Head> inserisce il contenuto degli <script> senza escaping: il
// JSON-LD include testi redazionali (titoli, descrizioni), quindi `<` va
// neutralizzato per impedire la chiusura anticipata del tag script.
const toJsonLdString = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c');

type PublicSeoProps = {
  title: string;
  description: string;
  path: string;
  imageUrl?: string | null;
  type?: 'website' | 'article';
  /** JSON-LD aggiuntivo specifico della pagina (es. BlogPosting, FAQPage). */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  children?: ReactNode;
};

export function PublicSeo({
  title,
  description,
  path,
  imageUrl = DEFAULT_IMAGE_URL,
  type = 'website',
  jsonLd,
  children,
}: PublicSeoProps) {
  const canonicalUrl = `${SITE_URL}${path}`;
  const jsonLdBlocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Head title={title}>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:site_name" content="Emovia" />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta name="twitter:card" content={imageUrl ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      <script head-key="jsonld-base" type="application/ld+json">
        {toJsonLdString(baseJsonLd)}
      </script>
      {jsonLdBlocks.map((block, index) => (
        <script
          key={`jsonld-${block['@type'] ?? index}`}
          head-key={`jsonld-${block['@type'] ?? index}`}
          type="application/ld+json"
        >
          {toJsonLdString(block)}
        </script>
      ))}
      {children}
    </Head>
  );
}
